/*
 * FamilyAcceptPage — the caregiver's front door.
 *
 * A senior's HAVN Browser shares an invite link (single-use, 7 days):
 *   /family/accept?token=<token>
 * This page: shows who's inviting -> signs the caregiver in or up with
 * their EMAIL (existing Supabase auth) -> accepts -> caregiver_links row
 * goes active -> shows today's activity summary immediately.
 *
 * Public route: handles both signed-out and signed-in arrivals.
 */

import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import GoogleSignInButton, { AuthDivider } from "@/components/GoogleSignInButton";

const API = "/api";

const FR = (navigator.language || "").toLowerCase().startsWith("fr");
const t = (en, fr) => (FR ? fr : en);

const S = {
  page: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--havn-bg, #0d1b33)",
    color: "var(--havn-text, #fff)",
    padding: "24px",
    textAlign: "center",
  },
  card: {
    width: "100%",
    maxWidth: "440px",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: "18px",
    padding: "28px 24px",
  },
  h1: { fontSize: "26px", fontWeight: 700, margin: "12px 0 6px" },
  p: { fontSize: "16px", opacity: 0.75, lineHeight: 1.5, margin: "6px 0" },
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "14px",
    fontSize: "16px",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.2)",
    background: "rgba(0,0,0,0.25)",
    color: "#fff",
    margin: "6px 0",
  },
  primary: {
    width: "100%",
    padding: "15px",
    fontSize: "17px",
    fontWeight: 600,
    borderRadius: "12px",
    border: "none",
    cursor: "pointer",
    background: "#ff5a2a",
    color: "#fff",
    marginTop: "14px",
  },
  ghost: {
    background: "none",
    border: "none",
    color: "rgba(255,255,255,0.7)",
    textDecoration: "underline",
    cursor: "pointer",
    fontSize: "15px",
    marginTop: "14px",
  },
  error: { color: "#ff8a65", fontSize: "15px", marginTop: "10px" },
  stat: {
    display: "inline-block",
    margin: "6px",
    padding: "10px 14px",
    borderRadius: "10px",
    background: "rgba(255,255,255,0.08)",
    fontSize: "15px",
  },
  orb: { width: "72px", height: "72px", margin: "0 auto" },
};

export default function FamilyAcceptPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { user, profile, session: supaSession, signIn, signUp } = useAuth();

  const [invite, setInvite] = useState(null); // {valid, senior_name, error}
  const [checking, setChecking] = useState(true);

  const [mode, setMode] = useState("signin"); // signin | signup
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [authError, setAuthError] = useState(null);
  const [authBusy, setAuthBusy] = useState(false);
  const [needsEmailConfirm, setNeedsEmailConfirm] = useState(false);

  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [seniorId, setSeniorId] = useState(null);
  const [activity, setActivity] = useState(null);

  const seniorLabel =
    invite?.senior_name || t("Your family member", "Votre proche");

  // ── 1. Validate the invite ────────────────────────────────────────────
  useEffect(() => {
    if (!token) {
      setInvite({ valid: false, error: "missing" });
      setChecking(false);
      return;
    }
    axios
      .get(`${API}/caregiver/invite/${token}`)
      .then((res) => setInvite(res.data))
      .catch(() => setInvite({ valid: false, error: "network" }))
      .finally(() => setChecking(false));
  }, [token]);

  // ── 3. Accept ─────────────────────────────────────────────────────────
  const accept = useCallback(async () => {
    if (!user || !token) return;
    setAccepting(true);
    setAcceptError(null);
    try {
      const name =
        profile?.full_name || user.user_metadata?.full_name || user.email;
      const res = await axios.post(`${API}/caregiver/invite/accept`, {
        token,
        caregiver_id: user.id,
        caregiver_name: name,
      });
      if (!res.data?.ok) {
        throw new Error(res.data?.error || "accept_failed");
      }
      setSeniorId(res.data.senior_id);
      setAccepted(true);
      // Remember locally so the portal can find this senior immediately.
      try {
        const key = "havn_linked_seniors";
        const list = JSON.parse(localStorage.getItem(key) || "[]");
        if (!list.find((l) => l.senior_id === res.data.senior_id)) {
          list.push({
            senior_id: res.data.senior_id,
            senior_name: invite?.senior_name || null,
            linked_at: new Date().toISOString(),
          });
          localStorage.setItem(key, JSON.stringify(list));
        }
      } catch (_) {}
      // Pull today's summary right away — via the authenticated dashboard
      // endpoint (the unauthenticated per-senior one is being retired).
      const tok = supaSession?.access_token;
      if (tok) {
        axios
          .get(`${API}/caregiver/my-dashboard`, {
            headers: { Authorization: `Bearer ${tok}` },
          })
          .then((a) => {
            const match = (a.data?.seniors || []).find(
              (s) => s.senior_id === res.data.senior_id
            );
            if (match?.today) setActivity(match.today);
          })
          .catch(() => {});
      }
    } catch (e) {
      const code = e?.message || "";
      setAcceptError(
        code === "already_used"
          ? t("This invitation was already used.", "Cette invitation a déjà été utilisée.")
          : code === "expired"
          ? t("This invitation has expired — ask for a new one.", "Cette invitation a expiré — demandez-en une nouvelle.")
          : t("Couldn't accept the invitation. Please try again.", "Impossible d'accepter l'invitation. Réessayez.")
      );
    } finally {
      setAccepting(false);
    }
  }, [user, token, profile, invite, supaSession]);

  // ── 2. Auth (inline, email-based) ─────────────────────────────────────
  const submitAuth = async (e) => {
    e.preventDefault();
    setAuthBusy(true);
    setAuthError(null);
    try {
      if (mode === "signup") {
        const data = await signUp(email.trim(), password, { full_name: fullName.trim() });
        if (!data?.session) {
          // Email confirmation is on — token stays valid for 7 days.
          setNeedsEmailConfirm(true);
          return;
        }
      } else {
        await signIn(email.trim(), password);
      }
      // useAuth().user updates via onAuthStateChange; accept happens on click.
    } catch (err) {
      setAuthError(err?.message || t("Sign-in failed.", "Échec de la connexion."));
    } finally {
      setAuthBusy(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────
  const shell = (children) => (
    <div style={S.page}>
      <div style={S.card}>
        <img src="/orb_havn.png" alt="" style={S.orb} onError={(e) => (e.target.style.display = "none")} />
        {children}
      </div>
    </div>
  );

  if (checking) {
    return shell(<p style={S.p}>{t("Checking your invitation…", "Vérification de votre invitation…")}</p>);
  }

  if (!invite?.valid) {
    const msg =
      invite?.error === "already_used"
        ? t("This invitation was already used.", "Cette invitation a déjà été utilisée.")
        : invite?.error === "expired"
        ? t("This invitation has expired.", "Cette invitation a expiré.")
        : t("This invitation link isn't valid.", "Ce lien d'invitation n'est pas valide.");
    return shell(
      <>
        <h1 style={S.h1}>{t("Invitation not available", "Invitation non disponible")}</h1>
        <p style={S.p}>{msg}</p>
        <p style={S.p}>
          {t(
            "Ask your family member to send a fresh invitation from their HAVN Browser.",
            "Demandez à votre proche d'envoyer une nouvelle invitation depuis son navigateur HAVN."
          )}
        </p>
      </>
    );
  }

  if (accepted) {
    return shell(
      <>
        <h1 style={S.h1}>✓ {t("You're connected", "Vous êtes connecté")}</h1>
        <p style={S.p}>
          {t(
            `You are now ${seniorLabel}'s HAVN family contact. You'll see safety alerts — never their private browsing.`,
            `Vous êtes maintenant le contact famille HAVN de ${seniorLabel}. Vous verrez les alertes de sécurité — jamais sa navigation privée.`
          )}
        </p>
        {activity && !activity.error && (
          <div style={{ marginTop: "14px" }}>
            <p style={{ ...S.p, fontWeight: 600, opacity: 0.9 }}>
              {t("Today so far", "Aujourd'hui")}
            </p>
            <span style={S.stat}>
              {t("Sites checked", "Sites vérifiés")}: {activity.total_sites ?? 0}
            </span>
            <span style={S.stat}>
              {t("Warnings", "Avertissements")}: {(activity.caution_count ?? 0) + (activity.danger_count ?? 0)}
            </span>
            <span style={S.stat}>
              {t("Overrides", "Ignorés")}: {activity.override_count ?? 0}
            </span>
          </div>
        )}
        <Link to="/caregiver-portal">
          <button style={S.primary}>
            {t("Open the caregiver portal", "Ouvrir le portail des proches")}
          </button>
        </Link>
      </>
    );
  }

  if (user) {
    return shell(
      <>
        <h1 style={S.h1}>
          {t(`${seniorLabel} invited you`, `${seniorLabel} vous a invité`)}
        </h1>
        <p style={S.p}>
          {t(
            "Accept to become their HAVN family contact. You'll get safety alerts and a daily summary — their private browsing stays private.",
            "Acceptez pour devenir son contact famille HAVN. Vous recevrez les alertes de sécurité et un résumé quotidien — sa navigation privée reste privée."
          )}
        </p>
        <button style={S.primary} onClick={accept} disabled={accepting}>
          {accepting
            ? t("Connecting…", "Connexion…")
            : t("Accept invitation", "Accepter l'invitation")}
        </button>
        {acceptError && <div style={S.error}>{acceptError}</div>}
        <p style={{ ...S.p, fontSize: "13px", marginTop: "14px" }}>
          {t("Signed in as", "Connecté en tant que")} {user.email}
        </p>
      </>
    );
  }

  if (needsEmailConfirm) {
    return shell(
      <>
        <h1 style={S.h1}>{t("Check your email", "Vérifiez vos courriels")}</h1>
        <p style={S.p}>
          {t(
            "We sent a confirmation link to your email. Confirm your account, then open this invitation link again — it stays valid for 7 days.",
            "Nous avons envoyé un lien de confirmation par courriel. Confirmez votre compte, puis rouvrez ce lien d'invitation — il reste valide 7 jours."
          )}
        </p>
      </>
    );
  }

  return shell(
    <>
      <h1 style={S.h1}>
        {t(`${seniorLabel} invited you`, `${seniorLabel} vous a invité`)}
      </h1>
      <p style={S.p}>
        {t(
          "To become their HAVN family contact, sign in with your email — or create a free account.",
          "Pour devenir son contact famille HAVN, connectez-vous avec votre courriel — ou créez un compte gratuit."
        )}
      </p>
      <GoogleSignInButton
        label={t("Continue with Google", "Continuer avec Google")}
        // Come back to THIS invite (the ?token= must survive the round trip).
        redirectTo={typeof window !== "undefined" ? window.location.href : undefined}
        onError={setAuthError}
      />
      <AuthDivider label={t("or", "ou")} color="rgba(255,255,255,0.45)" />

      <form onSubmit={submitAuth}>
        {mode === "signup" && (
          <input
            style={S.input}
            placeholder={t("Your name", "Votre nom")}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
          />
        )}
        <input
          style={S.input}
          type="email"
          placeholder={t("Email", "Courriel")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <input
          style={S.input}
          type="password"
          placeholder={t("Password", "Mot de passe")}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={6}
        />
        <button style={S.primary} type="submit" disabled={authBusy}>
          {authBusy
            ? t("One moment…", "Un instant…")
            : mode === "signup"
            ? t("Create account & continue", "Créer le compte et continuer")
            : t("Sign in & continue", "Se connecter et continuer")}
        </button>
      </form>
      {authError && <div style={S.error}>{authError}</div>}
      <button
        style={S.ghost}
        onClick={() => {
          setMode(mode === "signin" ? "signup" : "signin");
          setAuthError(null);
        }}
      >
        {mode === "signin"
          ? t("New here? Create an account", "Nouveau ici ? Créez un compte")
          : t("Already have an account? Sign in", "Déjà un compte ? Connectez-vous")}
      </button>
    </>
  );
}
